<script lang="ts">
  import { app, persistSettings } from './store.svelte';
  import { t } from './i18n';
  import { BUILTIN_PROFILES, profilesByVendor } from '../core/printerProfiles';
  import { loadCustomProfiles, addOrUpdateCustom, removeCustom, makeCustomId } from './customProfiles';
  import Icon from './lib/Icon.svelte';

  let showAdvanced = $state(false);
  const grouped = profilesByVendor();
  let customs = $state(loadCustomProfiles());

  function isCustom(id: string): boolean { return id.startsWith('custom-'); }

  function onPrinterChange(e: Event) {
    const id = (e.target as HTMLSelectElement).value;
    const all = [...BUILTIN_PROFILES, ...customs];
    const p = all.find((x) => x.id === id);
    if (p) { app.printer = { ...p }; persistSettings(); }
  }

  function updateField<K extends keyof typeof app.printer>(key: K, value: (typeof app.printer)[K]) {
    app.printer = { ...app.printer, [key]: value };
    // If editing a custom profile, persist the change to the saved copy too.
    if (isCustom(app.printer.id)) {
      customs = addOrUpdateCustom({ ...app.printer });
    }
    persistSettings();
  }

  function saveAsCustom() {
    const name = prompt('Profil adı?', `${app.printer.name} (özel)`);
    if (!name) return;
    const id = makeCustomId(name);
    const newP = { ...app.printer, id, name, vendor: 'Özel' };
    customs = addOrUpdateCustom(newP);
    app.printer = newP;
    persistSettings();
  }

  function deleteCurrent() {
    if (!isCustom(app.printer.id)) return;
    if (!confirm(`"${app.printer.name}" silinsin mi?`)) return;
    customs = removeCustom(app.printer.id);
    app.printer = { ...BUILTIN_PROFILES[0] };
    persistSettings();
  }
</script>

<section class="block">
  <div class="section-title"><Icon name="settings" size={11} /> Yazıcı</div>
  <div class="param-row">
    <span class="label">Model</span>
    <select value={app.printer.id} onchange={onPrinterChange}>
      {#each grouped as g (g.vendor)}
        <optgroup label={g.vendor}>
          {#each g.profiles as p (p.id)}
            <option value={p.id}>{p.name}</option>
          {/each}
        </optgroup>
      {/each}
      {#if customs.length > 0}
        <optgroup label="Özel">
          {#each customs as p (p.id)}
            <option value={p.id}>{p.name}</option>
          {/each}
        </optgroup>
      {/if}
    </select>
  </div>
  <div class="profile-actions">
    <button class="ghost" onclick={saveAsCustom} title="Mevcut ayarları yeni özel profil olarak kaydet">
      <Icon name="save" size={12} /> Farklı Kaydet
    </button>
    {#if isCustom(app.printer.id)}
      <button class="danger" onclick={deleteCurrent} title="Bu özel profili sil">
        <Icon name="trash" size={12} /> Sil
      </button>
    {/if}
  </div>
  <div class="param-row">
    <span class="label">Build</span>
    <span class="value">{app.printer.buildVolume.x}×{app.printer.buildVolume.y}×{app.printer.buildVolume.z}</span>
  </div>
  <div class="param-row">
    <span class="label">Nozzle</span>
    <span class="value">{app.printer.nozzleDiameter}mm</span>
  </div>
  <div class="param-row">
    <span class="label">Mod</span>
    <span class="value">{app.printer.extrusionMode === 'relative' ? 'M83' : 'M82'}</span>
  </div>

  <button class="adv-toggle" onclick={() => (showAdvanced = !showAdvanced)}>
    <span class="chev" class:open={showAdvanced}><Icon name="play" size={9} /></span>
    Gelişmiş Ayarlar
  </button>

  {#if showAdvanced}
    <div class="adv">
      <div class="param-row">
        <span class="label">Nozzle Çapı</span>
        <input type="number" min="0.4" max="6" step="0.1" value={app.printer.nozzleDiameter}
          oninput={(e) => updateField('nozzleDiameter', parseFloat((e.currentTarget as HTMLInputElement).value) || 2)} />
      </div>
      <div class="param-row">
        <span class="label">Şırınga Çapı</span>
        <input type="number" min="5" max="60" step="0.5" value={app.printer.filamentDiameter}
          oninput={(e) => updateField('filamentDiameter', parseFloat((e.currentTarget as HTMLInputElement).value) || 18)} />
      </div>
      <div class="param-row">
        <span class="label">Akış</span>
        <input type="number" min="0.5" max="2" step="0.05" value={app.printer.flowMultiplier}
          oninput={(e) => updateField('flowMultiplier', parseFloat((e.currentTarget as HTMLInputElement).value) || 1)} />
      </div>
      <div class="param-row">
        <span class="label">Mod</span>
        <select value={app.printer.extrusionMode}
          onchange={(e) => updateField('extrusionMode', (e.currentTarget as HTMLSelectElement).value as 'absolute' | 'relative')}>
          <option value="relative">M83</option>
          <option value="absolute">M82</option>
        </select>
      </div>

      <details>
        <summary>Başlangıç G-code</summary>
        <textarea rows="6" value={app.printer.startGcode}
          oninput={(e) => updateField('startGcode', (e.currentTarget as HTMLTextAreaElement).value)}></textarea>
      </details>

      <details>
        <summary>Bitiş G-code</summary>
        <textarea rows="5" value={app.printer.endGcode}
          oninput={(e) => updateField('endGcode', (e.currentTarget as HTMLTextAreaElement).value)}></textarea>
      </details>
    </div>
  {/if}
</section>

<style>
  .block { display: flex; flex-direction: column; }
  .section-title {
    font-size: 10px; color: var(--text-3);
    text-transform: uppercase; letter-spacing: 0.1em;
    font-weight: 600; margin-bottom: 8px;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .param-row {
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px; padding: 7px 0;
    border-bottom: 1px dashed var(--border-dashed);
  }
  .param-row:last-child { border-bottom: none; }
  .label { color: var(--text-2); font-size: 12px; }
  .value { color: var(--text-bright); font-weight: 600; font-size: 12px; }
  select, input[type='number'] {
    padding: 4px 8px;
    background: var(--chip-bg); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 6px;
    font-size: 12px; font-family: inherit;
    min-width: 110px; text-align: right;
  }
  select:focus, input:focus { outline: none; border-color: var(--accent); }
  .adv-toggle {
    margin-top: 8px;
    background: transparent; border: none; color: var(--text-3);
    cursor: pointer; padding: 6px 0; font-size: 11px; font-family: inherit;
    display: inline-flex; align-items: center; gap: 6px;
    text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600;
  }
  .adv-toggle:hover { color: var(--text-bright); }
  .chev {
    display: inline-flex; width: 14px; height: 14px;
    align-items: center; justify-content: center;
    transition: transform 0.18s cubic-bezier(0.2, 0.8, 0.2, 1);
    color: var(--text-muted);
  }
  .chev.open { transform: rotate(90deg); color: var(--accent); }
  .adv { display: flex; flex-direction: column; padding-top: 4px; }
  details summary {
    list-style: none; cursor: pointer;
    padding: 6px 0; font-size: 11px; color: var(--text-3);
  }
  details summary::-webkit-details-marker { display: none; }
  details summary::before {
    content: '▸'; display: inline-block; width: 12px; color: var(--text-muted);
    transition: transform 0.15s;
  }
  details[open] summary::before { transform: rotate(90deg); }
  textarea {
    width: 100%; box-sizing: border-box; resize: vertical;
    background: var(--bg-viewer-from); color: var(--text-2);
    border: 1px solid var(--border-soft); border-radius: 6px;
    padding: 8px; font-family: ui-monospace, 'SF Mono', monospace; font-size: 10.5px;
    line-height: 1.45;
  }
  .profile-actions { display: flex; gap: 4px; margin: 6px 0 4px; }
  .profile-actions button {
    flex: 1; padding: 4px 8px; border-radius: 6px; cursor: pointer;
    font-size: 10.5px; font-family: inherit; font-weight: 500;
    display: inline-flex; align-items: center; justify-content: center; gap: 4px;
    border: 1px solid var(--border-soft);
  }
  .profile-actions .ghost { background: var(--chip-bg); color: var(--text-2); }
  .profile-actions .ghost:hover { background: var(--chip-bg-hover); color: var(--text-bright); }
  .profile-actions .danger { background: rgba(208, 74, 58, 0.15); color: #ffaaa0; border-color: rgba(208,74,58,0.35); }
  .profile-actions .danger:hover { background: rgba(208, 74, 58, 0.28); }
</style>
